import { useEffect, useId, useState } from "react";
import { useRenameShortLinkGroupMutation } from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";
import { MAX_GROUP_LENGTH } from "../utils";

const CHIP = "shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold transition";
const CHIP_ON = "bg-gray-900 text-white";
const CHIP_OFF = "bg-gray-100 text-gray-700 hover:bg-gray-200";

/**
 * 그룹 탭: 전체 · 그룹들 · 그룹 없음. 그룹을 고르면 이름 바꾸기·그룹 풀기를 할 수 있다.
 * value: undefined(전체) | null(그룹 없음) | 그룹 이름
 */
export function FgGroupTabs({ groups, ungrouped, value, onChange }) {
    const renameMutation = useRenameShortLinkGroupMutation();
    const [renaming, setRenaming] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => setRenaming(false), [value]);

    if (!groups.length) {
        return <p className="mt-3 text-xs text-gray-400">링크를 만들거나 수정할 때 <b className="font-semibold text-gray-500">그룹</b>을 정하면 여기서 그룹별로 모아 볼 수 있어요.</p>;
    }

    const all = groups.reduce((sum, g) => sum + g.count, ungrouped);
    const selectedGroup = typeof value === 'string' ? value : null;

    const handleRename = (e) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name || name === selectedGroup) {
            setRenaming(false);
            return;
        }
        if (groups.some(g => g.name === name) && !window.confirm(`"${name}" 그룹이 이미 있어요. 두 그룹을 합칠까요?`)) {
            return;
        }
        renameMutation.mutate({ body: { name: selectedGroup, newName: name } }, { onSuccess: () => onChange(name) });
    }

    const handleUngroup = () => {
        if (!window.confirm(`"${selectedGroup}" 그룹을 풀까요? 링크는 지워지지 않고 '그룹 없음'으로 옮겨져요.`)) {
            return;
        }
        renameMutation.mutate({ body: { name: selectedGroup, newName: null } }, { onSuccess: () => onChange(undefined) });
    }

    const tab = (key, label, count, tabValue) => (
        <button key={key} type="button" aria-pressed={value === tabValue} onClick={() => onChange(tabValue)} className={`${CHIP} ${value === tabValue ? CHIP_ON : CHIP_OFF}`}>
            {label} <span className={value === tabValue ? 'text-white/70' : 'text-gray-400'}>{count}</span>
        </button>
    );

    return (
        <div className="mt-3 flex flex-col gap-2">
            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" role="group" aria-label="그룹">
                {tab('all', '전체', all, undefined)}
                {groups.map(g => tab(`g:${g.name}`, g.name, g.count, g.name))}
                {ungrouped > 0 && tab('none', '그룹 없음', ungrouped, null)}
            </div>

            {selectedGroup && !renaming &&
                <div className="flex items-center gap-3 text-xs">
                    <button type="button" onClick={() => { setNewName(selectedGroup); setRenaming(true); }} className="font-semibold text-gray-500 hover:text-gray-900 hover:underline">그룹 이름 바꾸기</button>
                    <button type="button" onClick={handleUngroup} className="font-semibold text-gray-500 hover:text-red-600 hover:underline">그룹 풀기</button>
                </div>
            }
            {selectedGroup && renaming &&
                <form onSubmit={handleRename} className="flex items-center gap-2">
                    <input
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        maxLength={MAX_GROUP_LENGTH}
                        aria-label="새 그룹 이름"
                        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[var(--mainColor)] min-[600px]:max-w-[240px] min-[600px]:flex-none"
                    />
                    <button type="submit" disabled={!newName.trim()} className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white disabled:bg-gray-300">저장</button>
                    <button type="button" onClick={() => setRenaming(false)} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold">취소</button>
                </form>
            }
        </div>
    );
}

/** 그룹 입력: 기존 그룹은 목록에서 고르고, 새 이름을 쓰면 새 그룹이 된다 */
export function FgGroupField({ value, onChange, groups }) {
    const listId = useId();

    return (
        <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-600">그룹 <span className="font-normal text-gray-400">선택 · 고르거나 새 이름을 쓰면 만들어져요</span></span>
            <input
                list={listId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                maxLength={MAX_GROUP_LENGTH}
                placeholder="예: 가을 세일, 협찬"
                className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)]"
            />
            <datalist id={listId}>
                {groups.map(g => <option key={g.name} value={g.name} />)}
            </datalist>
        </label>
    );
}
